import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { offerteAanmaakBewijs } from '../../lib/offerte-resultaat';
import { vervolgopdrachtResultaat } from '../../lib/vervolgopdracht-resultaat';
import { leesFactuurDocumenten } from '../../lib/factuurconcept';
import { leesOfferteDocumenten } from '../../lib/offerteconcept';
import { leesPartnerConcept } from '../../lib/partnerconcept';
import { leesProductNalevering } from '../../lib/productnalevering';
import { AFDOENING_VERB, leesMailAfdoening } from '../../lib/mail-afdoening';
import {
  actieLabel,
  modusBadge,
  AFWIJS_REDENEN,
  type AfwijsRedenCode,
  type MailVerbModi,
  type VoorgesteldeActie,
} from '../../lib/mail-acties';

/**
 * Kaart per voorgestelde mail-agent-actie (trede 2, klik-modus).
 *
 * Toont label, het citaat uit de mail, de preview uit de handler (wat er bij
 * akkoord precies gebeurt) en, in modus 'voorbereiden', de knoppen Akkoord en
 * Afwijzen (reden verplicht). Een `blokkade` in de preview = "kan niet", maar
 * afwijzen blijft mogelijk. plan_afspraak toont de dag-opties; akkoord stuurt het
 * gekozen optienummer mee. Na een besluit toont de kaart de uitkomst.
 *
 * Alleen getoonde planningdagen en historische klachtritten zijn kiesbaar;
 * vrije params-editing is niet toegestaan.
 */
export interface ActieKaartProps {
  actie: VoorgesteldeActie;
  index: number;
  modus: MailVerbModi[string] | undefined;
  busy: boolean;
  executing?: boolean;
  vraagBlokkade?: string | null;
  /**
   * V3 (14-09): één regel wat er gebeurt, knoppen Ja/Nee, de rest (citaat,
   * details, chauffeur) achter "Meer". Een medewerker beslist in twee seconden.
   */
  compact?: boolean;
  onVerversFactuur?: (index: number) => Promise<void>;
  onHeropenAfdoening?: (index: number) => Promise<void>;
  onBesluit: (
    index: number,
    besluit: 'akkoord' | 'afgewezen',
    reden?: { code: AfwijsRedenCode; tekst?: string },
    keuze?: { optie_nummer?: number; klacht_rit_id?: string; klacht_keuze_gecontroleerd?: boolean },
  ) => Promise<void>;
}

export function ActieKaart({ actie, index, modus, busy, executing = false, vraagBlokkade, compact = false, onBesluit, onVerversFactuur, onHeropenAfdoening }: ActieKaartProps) {
  const [afwijzen, setAfwijzen] = useState(false);
  const [redenCode, setRedenCode] = useState<AfwijsRedenCode>('verkeerde_gegevens');
  const [redenTekst, setRedenTekst] = useState('');
  const [optie, setOptie] = useState<number | null>(actie.preview?.opties?.[0]?.nummer ?? null);
  const [klachtRit, setKlachtRit] = useState(actie.preview?.klachtChauffeur?.voorgesteldRitId ?? '');
  const [klachtKeuzeGecontroleerd, setKlachtKeuzeGecontroleerd] = useState(false);
  const keuzeSnapshot = JSON.stringify({ params: actie.params, preview: actie.preview });
  useEffect(() => setKlachtKeuzeGecontroleerd(false), [keuzeSnapshot]);
  const chauffeurSnapshot = JSON.stringify(actie.preview?.klachtChauffeur ?? null);
  useEffect(() => {
    setKlachtRit(actie.preview?.klachtChauffeur?.voorgesteldRitId ?? '');
  }, [chauffeurSnapshot]);

  const preview = actie.preview;
  const uitkomst = actie.uitkomst;
  const offerte = uitkomst?.besluit === 'akkoord' ? offerteAanmaakBewijs(actie.verb, { ...uitkomst }) : undefined;
  const vervolg = uitkomst?.besluit === 'akkoord' ? vervolgopdrachtResultaat(actie.verb, { ...uitkomst }) : {};
  const bewezenGeslaagd = uitkomst?.ok === true && (actie.verb !== 'maak_vervolgopdracht'
    || Boolean(vervolg.opdracht && (vervolg.bevestiging?.status === 'verstuurd'
      || (vervolg.bevestiging?.status === 'wacht_op_verzendklik' && vervolg.opdracht.planningUrl))))
    && (actie.verb !== 'bereid_factuurconcept' || Boolean(leesFactuurDocumenten(uitkomst.documenten)))
    && (actie.verb !== 'bereid_offerteconcept' || Boolean(leesOfferteDocumenten(uitkomst.offerteDocumenten)))
    && (actie.verb !== 'bereid_partnerdoorverwijzing' || Boolean(leesPartnerConcept(uitkomst.partnerconcept)))
    && (actie.verb !== 'plan_productnalevering' || Boolean(leesProductNalevering(uitkomst.nalevering)))
    && (actie.verb !== AFDOENING_VERB || Boolean(leesMailAfdoening(uitkomst.afdoening)));
  const beoordelingAangevraagd = actie.verb === 'vraag_klachtbeoordeling' && uitkomst?.besluit === 'akkoord' && uitkomst.ok === true;
  const inUitvoering = executing || Boolean(actie.uitvoering && !uitkomst);
  const beslisbaar = !uitkomst && !inUitvoering;
  const actieMislukt = uitkomst?.besluit === 'akkoord' && !bewezenGeslaagd;
  const partnerHerstel = actie.verb === 'bereid_partnerdoorverwijzing' ? uitkomst?.partner_herstel : undefined;
  const partnerPreWriteMislukt = actieMislukt && partnerHerstel?.status === 'pre_write_mislukt';
  const klikbaar = modus === 'voorbereiden' && beslisbaar && !preview?.blokkade && !vraagBlokkade;
  const isPlanAfspraak = actie.verb === 'plan_afspraak';
  const badge = modusBadge(modus);

  const akkoordDisabled = busy || (isPlanAfspraak && (!preview?.opties?.length || optie === null))
    || (actie.verb === 'sluit_mail_zonder_antwoord' && !/^[a-f0-9]{64}$/.test(preview?.afdoeningBron || ''))
    || (actie.verb === 'bereid_partnerdoorverwijzing' && !/^[a-f0-9]{64}$/.test(preview?.partnerBron || ''))
    || (actie.verb === 'plan_productnalevering' && !/^[a-f0-9]{64}$/.test(preview?.naleveringBron || ''))
    || (actie.verb === 'maak_vervolgopdracht' && !/^[a-f0-9]{64}$/.test(preview?.vervolgopdrachtBron || ''))
    || ((actie.verb === 'bereid_factuurconcept' || actie.verb === 'corrigeer_factuur') && !/^[a-f0-9]{64}$/.test(preview?.factuurBron || ''))
    || (actie.verb === 'bereid_offerteconcept' && !/^[a-f0-9]{64}$/.test(preview?.offerteBron || ''))
    || (actie.verb === 'wijzig_order_items' && !/^[a-f0-9]{64}$/.test(preview?.orderwijzigingBron || ''))
    || (actie.verb === 'corrigeer_offerte' && !/^[a-f0-9]{64}$/.test(preview?.offertewijzigingBron || ''))
    || (actie.verb === 'verwerk_klachtkeuze' && (!preview || !klachtKeuzeGecontroleerd));

  return (
    <div
      className={clsx(
        'rounded-lg border px-3 py-2',
        compact ? 'text-sm' : 'text-xs',
        uitkomst?.besluit === 'akkoord' && bewezenGeslaagd ? 'border-emerald-200 bg-emerald-50' :
        uitkomst?.besluit === 'akkoord' ? 'border-red-200 bg-red-50' :
        uitkomst?.besluit === 'afgewezen' ? 'border-slate-200 bg-slate-50' :
        preview?.blokkade ? (compact ? 'border-amber-200 bg-amber-50' : 'border-red-200 bg-red-50') :
        'border-indigo-200 bg-white',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {/* Compact: bij een opmerking staat de inhoud al in de samenvatting; de kop blijft dan kort. */}
          <div className="font-medium text-slate-800">{compact && actie.verb === 'plaats_opmerking' ? actieLabel(actie).replace(/:\s[\s\S]*$/, '') : actieLabel(actie)}</div>
          {actie.reden && !compact && <div className="text-slate-500 italic mt-0.5">Uit de mail: &ldquo;{actie.reden}&rdquo;</div>}
        </div>
        {!uitkomst && !compact && (
          <span className="shrink-0 px-2 py-0.5 rounded font-medium bg-slate-100 text-slate-500 cursor-default" title={badge.title}>
            {inUitvoering ? 'bezig met verwerken' : badge.tekst}
          </span>
        )}
        {!uitkomst && compact && inUitvoering && <span className="shrink-0 text-xs text-slate-500">bezig...</span>}
      </div>

      {actieMislukt && (
        <div role="alert" className="mt-2 rounded-md border border-red-300 bg-red-100 px-3 py-2 text-red-900">
          <div className="font-semibold">Actie mislukt</div>
          <div>{uitkomst?.uitleg || 'De uitvoering kon niet worden bevestigd.'}</div>
          {partnerPreWriteMislukt && <div className="mt-1 font-medium">Er is niets aangemaakt of verzonden.</div>}
        </div>
      )}

      {preview && (
        <div className="mt-1.5">
          {preview.blokkade ? (
            <div className={compact ? 'text-amber-800' : 'text-red-700'}>Kan niet: {preview.blokkade}</div>
          ) : (
            <div className="text-slate-700">{preview.samenvatting}</div>
          )}
          {preview.details && preview.details.length > 0 && (actieMislukt ? (
            <details className="mt-1 text-slate-500">
              <summary className="cursor-pointer">Oude previewdetails tonen</summary>
              <ul className="mt-0.5 list-disc pl-4">
                {preview.details.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
            </details>
          ) : compact ? (
            <details className="mt-1 text-xs text-slate-500">
              <summary className="cursor-pointer select-none">Meer</summary>
              {actie.reden && <div className="mt-0.5 italic">Uit de mail: &ldquo;{actie.reden}&rdquo;</div>}
              <ul className="mt-0.5 list-disc pl-4">
                {preview.details.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
              {actie.verb === 'registreer_klacht' && preview.klachtChauffeur && !uitkomst && (
                <label className="mt-2 flex flex-col gap-1 text-slate-700">
                  Betrokken chauffeur
                  <select aria-label="Betrokken chauffeur" value={klachtRit} onChange={e => setKlachtRit(e.target.value)} disabled={!klikbaar || busy}
                    className="rounded border border-slate-300 bg-white px-2 py-1">
                    <option value="">Geen chauffeurkoppeling (onbekend / niet van toepassing)</option>
                    {preview.klachtChauffeur.opties.map(o => <option key={o.ritId} value={o.ritId}>{o.naam} · {o.kant} · {o.datum}</option>)}
                  </select>
                  <span className="text-slate-500">{preview.klachtChauffeur.toelichting}</span>
                </label>
              )}
            </details>
          ) : (
            <ul className="mt-0.5 list-disc pl-4 text-slate-500">
              {preview.details.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
          ))}
          {actie.verb === 'registreer_klacht' && preview.klachtChauffeur && !uitkomst && !(compact && preview.details && preview.details.length > 0) && (
            <label className="mt-2 flex flex-col gap-1 text-slate-700">
              Betrokken chauffeur
              <select aria-label="Betrokken chauffeur" value={klachtRit} onChange={e => setKlachtRit(e.target.value)} disabled={!klikbaar || busy}
                className="rounded border border-slate-300 bg-white px-2 py-1">
                <option value="">Geen chauffeurkoppeling (onbekend / niet van toepassing)</option>
                {preview.klachtChauffeur.opties.map(o => <option key={o.ritId} value={o.ritId}>{o.naam} · {o.kant} · {o.datum}</option>)}
              </select>
              <span className="text-slate-500">{preview.klachtChauffeur.toelichting}</span>
            </label>
          )}
          {isPlanAfspraak && !uitkomst && preview.opties && preview.opties.length > 0 && (
            <div className="mt-1.5 flex flex-col gap-1">
              {preview.opties.map((o) => (
                <label key={o.nummer} className="flex items-center gap-2 text-slate-700 cursor-pointer">
                  <input
                    type="radio"
                    name={`actie-${index}-optie`}
                    checked={optie === o.nummer}
                    onChange={() => setOptie(o.nummer)}
                    disabled={!klikbaar || busy}
                  />
                  <span>{o.nummer}. {o.dag}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}
      {!preview && !uitkomst && <div className="mt-1 text-slate-400">Preview niet beschikbaar; bij akkoord worden alle controles alsnog gedaan.</div>}
      {vraagBlokkade && !uitkomst && <div className="mt-1 text-amber-800">{vraagBlokkade}</div>}
      {actie.verb === 'verwerk_klachtkeuze' && klikbaar && <label className="mt-2 flex items-start gap-2 text-slate-700">
        <input type="checkbox" checked={klachtKeuzeGecontroleerd} onChange={e => setKlachtKeuzeGecontroleerd(e.target.checked)} disabled={busy} />
        <span>Ik heb de volledige klantreactie gelezen. Deze kiest onvoorwaardelijk bij dit aanbod, dit kleed en alle genoemde risico’s.</span>
      </label>}

      {uitkomst && (
        <div className="mt-1.5 flex items-center gap-2">
          <span
            className={clsx(
              'px-2 py-0.5 rounded font-medium',
              uitkomst.besluit === 'akkoord' && bewezenGeslaagd ? 'bg-emerald-100 text-emerald-700' :
              uitkomst.besluit === 'akkoord' ? 'bg-red-100 text-red-700' :
              'bg-slate-200 text-slate-600',
            )}
            title={uitkomst.uitleg || undefined}
          >
            {uitkomst.besluit === 'akkoord' && bewezenGeslaagd && (actie.verb === 'plan_productnalevering' ? 'nalevering klaargezet; nog niet verzonden' : actie.verb === AFDOENING_VERB ? 'afgehandeld; geen mail verstuurd' : actie.verb === 'bereid_partnerdoorverwijzing' ? 'aanvraag voorbereid; verzenden apart' : actie.verb === 'corrigeer_offerte' ? (actie.params.alleen_prijs === true ? 'prijsvoorstel klaar; offerte ongewijzigd' : 'offerte gecorrigeerd; verzenden apart') : actie.verb === 'wijzig_order_items' && actie.params.alleen_prijs === true ? 'prijsvoorstel klaar; order ongewijzigd' : ['bereid_factuurconcept','bereid_offerteconcept'].includes(actie.verb) ? 'bijlagen voorbereid; nog niet verzonden' : vervolg.opdracht ? (vervolg.bevestiging?.status === 'wacht_op_verzendklik' ? 'opdracht aangemaakt; mail apart verzenden' : 'opdracht aangemaakt; bevestiging verstuurd') : offerte ? 'offerte aangemaakt' : beoordelingAangevraagd ? 'beoordeling aangevraagd' : uitkomst.klacht?.afhandeling === 'antwoord_volstaat' ? 'geregistreerd; afhandelen na verzenden' : 'uitgevoerd')}
            {uitkomst.besluit === 'akkoord' && !bewezenGeslaagd && `${vervolg.opdracht ? 'opdracht aangemaakt; controle nodig' : 'mislukt'}: ${uitkomst.uitleg || (actie.verb === 'maak_vervolgopdracht' ? 'uitvoering niet bevestigd' : 'onbekend')}`}
            {uitkomst.besluit === 'afgewezen' && `afgewezen: ${AFWIJS_REDENEN.find((r) => r.code === uitkomst.reden?.code)?.label ?? uitkomst.reden?.code ?? ''}${uitkomst.reden?.tekst ? ` — ${uitkomst.reden.tekst}` : ''}`}
          </span>
          <span className="text-slate-400">{uitkomst.door}</span>
          {actie.verb === AFDOENING_VERB && bewezenGeslaagd && onHeropenAfdoening && (
            <button type="button" disabled={busy} onClick={() => onHeropenAfdoening(index)}
              className="text-indigo-700 underline disabled:opacity-50"
              title="Heropen dit gesprek voor opvolging en maak een nieuwe beoordeling. Het oorspronkelijke akkoord blijft geregistreerd.">
              Heropen voor opvolging
            </button>
          )}
        </div>
      )}

      {beoordelingAangevraagd && (
        <div className="mt-1 text-slate-500">Het technische oordeel leg je apart vast.</div>
      )}

      {['bereid_factuurconcept','bereid_offerteconcept'].includes(actie.verb) && onVerversFactuur && modus === 'voorbereiden'
        && uitkomst?.besluit !== 'afgewezen' && !inUitvoering && (
        <div className="mt-2 text-slate-600">
          <button type="button" disabled={busy} onClick={() => void onVerversFactuur(index)}
            className="text-indigo-700 underline disabled:opacity-50">{actie.verb === 'bereid_offerteconcept' ? 'Offerte' : 'Factuur'} opnieuw voorbereiden</button>
          <p>Bij gewijzigde documentgegevens. Daarna opnieuw beoordelen; het eerdere besluit blijft in de historie.</p>
        </div>
      )}

      {actie.verb === 'bereid_partnerdoorverwijzing' && actieMislukt && (
        <div className="mt-2 text-red-900">
          {partnerPreWriteMislukt && onVerversFactuur && modus === 'voorbereiden' ? (
            <>
              <button type="button" disabled={busy} onClick={() => void onVerversFactuur(index)}
                className="rounded-md bg-red-700 px-3 py-1.5 font-medium text-white disabled:opacity-50">
                Opnieuw voorbereiden
              </button>
              <p className="mt-1">Dit maakt alleen een nieuwe preview. Daarna is opnieuw expliciet akkoord nodig.</p>
            </>
          ) : (
            <p className="font-medium">Controleer bestaande poging. Automatisch opnieuw uitvoeren is geblokkeerd.</p>
          )}
        </div>
      )}

      {uitkomst?.ok && uitkomst.klacht && (
        <div className="mt-1 flex flex-wrap gap-3 text-indigo-700 underline">
          <a href={`/klachten/${uitkomst.klacht.id}`}>Bekijk klacht {uitkomst.klacht.nummer}</a>
          {(() => {
            const v = (uitkomst as Record<string, unknown>).vervolg_wo as { id?: string; nummer?: string | null } | undefined;
            return v?.id ? <a href={`/orders/${v.id}`}>Vervolgopdracht {v.nummer ?? ''}</a> : null;
          })()}
        </div>
      )}

      {offerte && (
        <div className="mt-1 flex flex-wrap gap-3 text-indigo-700 underline">
          <a href={`/offertes/${offerte.id}`}>Bekijk offerte {offerte.nummer}</a>
          <a href={offerte.url} target="_blank" rel="noopener noreferrer">Klantlink</a>
        </div>
      )}
      {actie.verb === 'plan_productnalevering' && bewezenGeslaagd && uitkomst?.nalevering && (
        <div className="mt-1 text-slate-700">
          <a className="text-indigo-700 underline" href={`/orders/${uitkomst.nalevering.orderId}`}>Bekijk nalevering {uitkomst.nalevering.nummer}</a>
          <div>Klaargezet in de verzendwachtrij; nog niet verzonden. Het antwoord verzend je apart.</div>
        </div>
      )}
      {vervolg.opdracht && <div className="mt-1 text-slate-700">
        <a className="text-indigo-700 underline" href={`/orders/${vervolg.opdracht.id}`}>Bekijk opdracht {vervolg.opdracht.nummer}</a>
        <div>Orderbevestiging: {vervolg.bevestiging?.status === 'wacht_op_verzendklik' ? 'wacht op apart verzenden van het antwoord' : vervolg.bevestiging?.status === 'verstuurd' ? 'verstuurd (aflevering niet bevestigd)' : vervolg.bevestiging?.status === 'niet_verstuurd' ? 'niet verstuurd' : 'uitkomst onzeker'}. Geen ophaaldag geboekt.</div>
      </div>}

      {beslisbaar && !afwijzen && (
        <div className="mt-2 flex items-center gap-2">
          {klikbaar && <button
            type="button"
            disabled={akkoordDisabled}
            onClick={() => void onBesluit(index, 'akkoord', undefined, isPlanAfspraak && optie !== null ? { optie_nummer: optie }
              : actie.verb === 'verwerk_klachtkeuze' ? { klacht_keuze_gecontroleerd: klachtKeuzeGecontroleerd }
              : actie.verb === 'registreer_klacht' && preview?.klachtChauffeur ? { klacht_rit_id: klachtRit } : undefined)}
            className={clsx('rounded-md font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50', compact ? 'px-4 py-1.5' : 'px-3 py-1')}
          >
            {actie.verb === AFDOENING_VERB ? (compact ? 'Ja, sluiten zonder antwoord' : 'Akkoord, zonder antwoord sluiten') : actie.verb === 'maak_vervolgopdracht' ? (compact ? 'Ja, opdracht maken' : 'Opdracht maken; mail apart') : compact ? 'Ja, doen' : 'Akkoord'}
          </button>}
          <button
            type="button"
            disabled={busy}
            onClick={() => setAfwijzen(true)}
            className={clsx('rounded-md font-medium bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50', compact ? 'px-4 py-1.5' : 'px-3 py-1')}
          >
            {compact ? 'Nee' : 'Afwijzen'}
          </button>
        </div>
      )}

      {beslisbaar && afwijzen && (
        <div className="mt-2 flex flex-col gap-1.5">
          <select
            value={redenCode}
            onChange={(e) => setRedenCode(e.target.value as AfwijsRedenCode)}
            className="border border-slate-300 rounded-md px-2 py-1 bg-white text-slate-700"
          >
            {AFWIJS_REDENEN.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
          </select>
          <input
            type="text"
            value={redenTekst}
            onChange={(e) => setRedenTekst(e.target.value)}
            placeholder={redenCode === 'anders' ? 'Toelichting (verplicht)' : 'Toelichting (optioneel)'}
            className="border border-slate-300 rounded-md px-2 py-1 bg-white text-slate-700"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={busy || (redenCode === 'anders' && !redenTekst.trim())}
              onClick={() => void onBesluit(index, 'afgewezen', { code: redenCode, tekst: redenTekst.trim() || undefined })}
              className="px-3 py-1 rounded-md font-medium bg-slate-700 text-white hover:bg-slate-800 disabled:opacity-50"
            >
              Afwijzen bevestigen
            </button>
            <button type="button" disabled={busy} onClick={() => setAfwijzen(false)} className="px-2 py-1 text-slate-500 hover:text-slate-700">
              Annuleren
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
